import { useState, useEffect } from "react";
import { Archive, Edit3, CheckCircle, XCircle, UserCircle, AlertCircle, Inbox, RefreshCw, ChevronUp, ChevronDown, Tag, User, Mail, Phone, Globe, Activity } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api";
import { Badge } from "../../../components/ui/Badge";
import { useNotification } from "../../../context/NotificationContext";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import { getPaginationState } from "../../../utils/pagination";
import Tooltip from "../../../components/ui/Tooltip";

const getStatusInfo = (status) => {
  switch (status) {
    case "CONVERTED":
      return { variant: "success", icon: <CheckCircle className="w-3.5 h-3.5" /> };
    case "DISQUALIFIED":
      return { variant: "destructive", icon: <XCircle className="w-3.5 h-3.5" /> };
    case "QUALIFIED":
      return { variant: "info", icon: <UserCircle className="w-3.5 h-3.5" /> };
    case "NEW":
      return { variant: "warning", icon: <AlertCircle className="w-3.5 h-3.5" /> };
    default:
      return { variant: "neutral", icon: <UserCircle className="w-3.5 h-3.5" /> };
  }
};

const formatPhone = (phone) => {
  if (!phone) return "-";
  const cleanPhone = phone.trim();
  if (cleanPhone.startsWith("+")) return cleanPhone;
  if (cleanPhone.startsWith("0")) return `+33 ${cleanPhone.substring(1)}`;
  return cleanPhone;
};

export default function LeadsGrid({ leads, selectProspect, onEdit, refreshLeads, selectedLeads, setSelectedLeads, onSyncFlowS, onArchiveLead, paginationResetKey }) {
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const { showToast } = useNotification();
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
  const filteredLeads = leads.filter((lead) => !lead.status || lead.status === "NEW" || lead.status === "PENDING");

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key] || "";
    let bValue = b[sortConfig.key] || "";

    if (sortConfig.key === 'lastName') {
      aValue = `${a.lastName || ''} ${a.firstName || ''}`.trim().toLowerCase();
      bValue = `${b.lastName || ''} ${b.firstName || ''}`.trim().toLowerCase();
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { safePage, startIndex, endIndex } = getPaginationState({
    page: currentPage,
    pageSize: itemsPerPage,
    totalItems: sortedLeads.length,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [paginationResetKey]);

  useEffect(() => {
    setCurrentPage((page) => getPaginationState({
      page,
      pageSize: itemsPerPage,
      totalItems: sortedLeads.length,
    }).safePage);
  }, [sortedLeads.length]);

  const currentLeads = sortedLeads.slice(startIndex, endIndex);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const SortableHeader = ({ label, sortKey, icon: Icon, className = "" }) => {
    return (
      <th
        className={`px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle cursor-pointer hover:bg-surface transition-colors ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1.5 ${className.includes('text-center') ? 'justify-center' : ''}`}>
          {Icon && <Icon className="w-3.5 h-3.5 text-tertiary" />}
          <span>{label}</span>
          {sortConfig.key === sortKey ? (
            sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 text-primary ml-1" /> : <ChevronDown className="w-3 h-3 text-primary ml-1" />
          ) : (
            <ChevronUp className="w-3 h-3 opacity-0 ml-1" />
          )}
        </div>
      </th>
    );
  };

  const handleArchive = async () => {
    if (!confirmArchive) return;
    setIsArchiving(true);
    try {
      if (onArchiveLead) {
        await onArchiveLead(confirmArchive.id || confirmArchive._id);
      } else {
        await api.put(`/leads/${confirmArchive.id || confirmArchive._id}`, { status: "DISQUALIFIED" });
        refreshLeads();
        showToast("Lead archivé avec succès", "success");
      }
    } catch (err) {
      console.error("Erreur lors de l'archivage :", err);
      showToast("Erreur lors de l'archivage", "error");
    } finally {
      setIsArchiving(false);
      setConfirmArchive(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(filteredLeads.map(l => l.id || l._id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectRow = (e, id) => {
    if (e.target.checked) {
      setSelectedLeads([...selectedLeads, id]);
    } else {
      setSelectedLeads(selectedLeads.filter(selectedId => selectedId !== id));
    }
  };

  return (
    <div className="flex min-w-0 flex-col flex-1 w-full bg-surface">
      <div className="flex-1 min-w-0 overflow-x-auto">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-[800px]">
          <thead className="bg-surface-muted border-b border-workspace sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-4 text-sm font-medium text-subtle text-center w-12">
                <input
                  type="checkbox"
                  className="rounded border-workspace focus:ring-2 focus:ring-accent accent-accent w-4 h-4 cursor-pointer"
                  checked={selectedLeads.length > 0 && selectedLeads.length === filteredLeads.length}
                  onChange={handleSelectAll}
                />
              </th>
              <SortableHeader label="Type" sortKey="type" icon={Tag} />
              <SortableHeader label="Nom complet" sortKey="lastName" icon={User} />
              <SortableHeader label="Email" sortKey="email" icon={Mail} />
              <SortableHeader label="Téléphone" sortKey="phone" icon={Phone} />
              <SortableHeader label="Source" sortKey="source" icon={Globe} />
              <SortableHeader label="Statut" sortKey="status" icon={Activity} className="text-center" />
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-workspace relative">
            <AnimatePresence mode="popLayout">
              {currentLeads.map((lead) => {
                const { variant, icon } = getStatusInfo(lead.status);
                const leadId = lead.id || lead._id;
                const isSelected = selectedLeads.includes(leadId);

                return (
                  <motion.tr
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, type: "spring", bounce: 0.2 }}
                    key={leadId}
                    className={`group transition-colors cursor-pointer border-b border-workspace/50 last:border-0 ${isSelected
                      ? 'bg-[rgb(var(--hp-ink-900-rgb)/6%)]'
                      : 'hover:bg-[rgb(var(--hp-ink-900-rgb)/4%)]'
                      }`}
                    onClick={() => selectProspect(lead)}
                  >
                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="rounded border-workspace focus:ring-2 focus:ring-accent accent-accent w-4 h-4 cursor-pointer"
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(e, lead.id || lead._id)}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-muted">{lead.organizationName ? "Personne Morale" : "Personne Physique"}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-primary">
                      {lead.lastName} {lead.firstName}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted">{lead.email || "-"}</td>
                    <td className="px-6 py-4 text-sm text-muted">{formatPhone(lead.phone)}</td>
                    <td className="px-6 py-4 text-sm text-muted">{lead.source || "-"}</td>
                    <td className="px-6 py-4 text-center">
                      <Badge variant={variant}>
                        {icon}
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">

                        <Tooltip content="Éditer">
                          <button
                            className="workspace-icon-button w-8 h-8 rounded-full"
                            onClick={(e) => { e.stopPropagation(); onEdit(lead); }}
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Archiver">
                          <button
                            className="workspace-icon-button w-8 h-8 rounded-full hover:bg-danger-50 hover:text-danger-600 hover:border-danger-200"
                            onClick={(e) => { e.stopPropagation(); setConfirmArchive(lead); }}
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>

            {filteredLeads.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-32 border-b-0">
                  <div className="flex flex-col items-center justify-center text-center">
                    <motion.div
                      animate={{ y: [0, -10, 0] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    >
                      <Inbox className="w-16 h-16 text-tertiary mb-6 mx-auto" strokeWidth={1.5} />
                    </motion.div>
                    <h3 className="text-lg font-bold text-primary mb-2">Aucun lead actif trouvé</h3>
                    <p className="text-subtle mb-8 max-w-sm whitespace-normal">
                      Vous n'avez aucun lead correspondant à votre recherche. Vous pouvez importer des leads ou les synchroniser depuis FlowS.
                    </p>
                    <button
                      onClick={onSyncFlowS}
                      className="btn btn-accent shadow-hp-sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Synchroniser les leads
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {sortedLeads.length > 0 && (
        <PaginationFooter
          page={safePage}
          pageSize={itemsPerPage}
          totalItems={sortedLeads.length}
          onPageChange={setCurrentPage}
          ariaLabel="Pagination des leads"
        />
      )}

      <AnimatePresence>
        {confirmArchive && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="w-16 h-16 rounded-full bg-danger-100 text-danger-600 flex items-center justify-center mb-6 shadow-sm">
                <Archive className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">Archiver ce lead ?</h2>
              <p className="text-subtle text-sm mb-8">
                Êtes-vous sûr de vouloir archiver <strong>{confirmArchive.lastName} {confirmArchive.firstName}</strong> ?
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  className="btn btn-secondary flex-1"
                  onClick={() => setConfirmArchive(null)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-danger flex-1"
                  onClick={handleArchive}
                  disabled={isArchiving}
                >
                  {isArchiving ? "Archivage..." : "Archiver"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
